import {
  BottomSheet,
  BottomSheetIllustrationContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { DialogContent } from "@mui/material";
import { Fragment, useCallback, useEffect, useState } from "react";

import { APP_V2_USER_EVENTS, logEvent } from "../../../../../../lib/analytics";
import { BottomSheetHeader } from "../../../../../components/BottomSheetHeader";
import { Button } from "../../../../../components/Button";
import { DialogFooter } from "../../../../../components/DialogFooter";
import { TextButton } from "../../../../../components/TextButton";
import { QuizResult } from "../types";

interface QuizNotPassedBottomSheetProps {
  modalState: UseModalState;
  quizResult: QuizResult;
  onClose: () => void;
  onRetakeQuiz: () => void;
}

export function QuizNotPassedBottomSheet(props: QuizNotPassedBottomSheetProps) {
  const { modalState, quizResult, onClose, onRetakeQuiz } = props;
  const { maxMistakesAllowed, canDisplayAnswers } = quizResult;

  const wrongAnswerCount = quizResult.numberOfIncorrectAnswers;
  const [isReviewingQuestions, setIsReviewingQuestions] = useState(false);

  useEffect(() => {
    logEvent(APP_V2_USER_EVENTS.PANDADOC_QUIZ_NOT_PASSED_VIEWED);
  }, []);

  const handleClose = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PANDADOC_QUIZ_NOT_PASSED_NOT_NOW_TAPPED);
    modalState.closeModal();
    onClose();
  }, [modalState, onClose]);

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={(theme) => ({
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
      })}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="vertical">
          {canDisplayAnswers && !isReviewingQuestions ? (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.PANDADOC_QUIZ_NOT_PASSED_REVIEW_QUESTIONS_TAPPED);
                setIsReviewingQuestions(true);
              }}
            >
              Review Questions
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.PANDADOC_QUIZ_NOT_PASSED_RETAKE_QUIZ_TAPPED);
                  onRetakeQuiz();
                }}
              >
                Retake Quiz
              </Button>
              <TextButton size="large" onClick={handleClose}>
                Not Now
              </TextButton>
            </>
          )}
        </DialogFooter>
      }
      onClose={handleClose}
    >
      {isReviewingQuestions ? (
        <DialogContent sx={{ overflow: "auto" }}>
          <List>
            {quizResult.result.map((entry) => (
              <Fragment key={entry.sectionName}>
                {entry.sectionName && (
                  <Text semibold variant="h4" sx={{ paddingY: 2, textAlign: "center" }}>
                    {entry.sectionName}
                  </Text>
                )}
                {entry.questions.map((item) => (
                  <ListItem
                    key={`${entry.sectionName}-${item.questionNumber}`}
                    sx={{ paddingY: 4 }}
                  >
                    <ListItemIcon
                      iconType={item.isCorrect ? "checkmark" : "close"}
                      color={(theme) =>
                        item.isCorrect ? theme.palette.success.main : theme.palette.error.main
                      }
                    />
                    <ListItemText
                      disableTypography
                      primary={
                        <Text semibold variant="body1">
                          Q{item.questionNumber} Correct answer:{" "}
                          {item.correctValues?.join(", ") ?? "Not provided"}
                        </Text>
                      }
                      secondary={
                        <Text
                          variant="body2"
                          sx={(theme) => ({
                            marginTop: 1,
                            color: item.isCorrect
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          })}
                        >
                          Your answer: {item.selectedValues.join(", ")}
                        </Text>
                      }
                    />
                  </ListItem>
                ))}
              </Fragment>
            ))}
          </List>
        </DialogContent>
      ) : (
        <BottomSheetIllustrationContent
          illustrationType="document-rejected"
          title="Quiz Failed"
          description={`You got ${wrongAnswerCount} ${wrongAnswerCount === 1 ? "question" : "questions"} wrong, but only ${maxMistakesAllowed} ${maxMistakesAllowed === 1 ? "mistake is" : "mistakes are"} allowed.${canDisplayAnswers ? " Take a look at what you got wrong and give it another shot." : ""}`}
        />
      )}
    </BottomSheet>
  );
}
